import api from '../http/api/api.js';
import constants from '../../constant.js';

const POLL_INTERVAL_MS = 5000;
const MAX_ATTEMPTS = 18;

export const meetingAnalyticsMixin = {
  data() {
    return {
      analyticsData: null,
      analyticsLoading: true,
      analyticsError: null,
      // Public meeting slug. Returned alongside the analytics payload
      // from /analytics/by-id/ and used as the cast key for every
      // downstream call (feedback, access-policy, etc).
      publicMeetingId: null,
      // Recording metadata for this specific session. null until the
      // BBB recording pipeline finishes processing (a few minutes
      // after the meeting ends).
      recording: null,
      recordingEnabled: false,
      // refused for want of storage, rather than simply never asked for
      recordingBlocked: false,
      sessionKind: 'cast',
      _analyticsAttempts: 0,
      _analyticsTimer: null,
    };
  },
  methods: {
    async _fetchAnalyticsOnce(analyticsId) {
      const url = `${constants.apiCastUrl}/api/event/meeting/analytics/by-id/`;
      return api.get(url, { params: { id: analyticsId } });
    },

    async fetchAccessPolicy(meetingId) {
      // Backend GET /api/event/meeting/access-policy/?meeting_id=<slug>
      // Returns { policy, is_host, has_access, required_credentials }
      // Frontend swallows errors so the page still renders if endpoint
      // is missing (treats as public_all + host).
      try {
        const url = `${constants.apiCastUrl}/api/event/meeting/access-policy/`;
        const res = await api.get(url, { params: { meeting_id: meetingId } });
        return res && res.data ? res.data : null;
      } catch (e) {
        return null;
      }
    },

    async submitPostCallFeedback(meetingId, payload) {
      const url = `${constants.apiCastUrl}/api/event/meeting/feedback/`;
      const body = {
        rating: payload.rating,
        message: payload.message,
        anonymous: !!payload.anonymous,
      };
      // Rooms have no cast slug - the backend resolves them via the
      // analytics id instead.
      if (meetingId) body.meeting_id = meetingId;
      else body.analytics_id = this.analyticsId;
      return api.post(url, body);
    },

    async getPostCallFeedbackStatus(meetingId) {
      try {
        const url = `${constants.apiCastUrl}/api/event/meeting/feedback/status/`;
        const params = meetingId
          ? { meeting_id: meetingId }
          : { analytics_id: this.analyticsId };
        const res = await api.get(url, { params });
        return res && res.data ? res.data : null;
      } catch (e) {
        return null;
      }
    },

    async requestVcAccessApi(meetingId, email) {
      const url = `${constants.apiCastUrl}/api/event/meeting/request-access/`;
      return api.post(url, { meeting_id: meetingId, email });
    },

    async presentCredentialsApi(meetingId, vpJwt) {
      const url = `${constants.apiCastUrl}/api/event/meeting/present-credentials/`;
      return api.post(url, { meeting_id: meetingId, vp_jwt: vpJwt });
    },

    async _pollAnalytics(analyticsId) {
      if (!analyticsId) {
        this.analyticsLoading = false;
        this.analyticsError = 'missing_analytics_id';
        return;
      }
      if (this._analyticsAttempts >= MAX_ATTEMPTS) {
        this.analyticsLoading = false;
        if (!this.analyticsData) this.analyticsError = 'unavailable';
        return;
      }
      this._analyticsAttempts += 1;

      try {
        const res = await this._fetchAnalyticsOnce(analyticsId);
        const body = (res && res.data) || {};
        this.analyticsData = body.analytics_data || null;
        this.publicMeetingId = body.public_meeting_id || null;
        this.recording = body.recording || null;
        this.recordingEnabled = !!body.recording_enabled;
        this.recordingBlocked = !!body.recording_blocked;
        this.sessionKind = body.kind || 'cast';
        this.analyticsLoading = false;
      } catch (err) {
        const status = err && err.response && err.response.status;
        const errMsg =
          err && err.response && err.response.data && err.response.data.error;

        if (status === 404 && errMsg === 'analytics pending') {
          // Row exists, data not pushed yet. Keep polling.
          this._analyticsTimer = setTimeout(
            () => this._pollAnalytics(analyticsId),
            POLL_INTERVAL_MS
          );
        } else if (status === 404) {
          // Unknown id. Stop and surface error.
          this.analyticsLoading = false;
          this.analyticsError = 'not_found';
        } else if (status === 401) {
          this.analyticsLoading = false;
          this.analyticsError = 'unauthorized';
        } else {
          this.analyticsLoading = false;
          this.analyticsError = 'failed';
        }
      }
    },

    startAnalyticsPolling(analyticsId) {
      this.analyticsData = null;
      this.publicMeetingId = null;
      this.recording = null;
      this.analyticsError = null;
      this.analyticsLoading = true;
      this._analyticsAttempts = 0;
      this._pollAnalytics(analyticsId);
    },

    async refetchAnalytics(analyticsId) {
      try {
        const res = await this._fetchAnalyticsOnce(analyticsId);
        const body = (res && res.data) || {};
        if (body.analytics_data) this.analyticsData = body.analytics_data;
        if (body.public_meeting_id)
          this.publicMeetingId = body.public_meeting_id;
        this.recording = body.recording || null;
        this.recordingEnabled = !!body.recording_enabled;
        this.recordingBlocked = !!body.recording_blocked;
        this.sessionKind = body.kind || 'cast';
      } catch (e) {
        // Silent on refresh; primary state already populated.
      }
    },

    stopAnalyticsPolling() {
      if (this._analyticsTimer) {
        clearTimeout(this._analyticsTimer);
        this._analyticsTimer = null;
      }
    },

    fmtMs(ms) {
      const s = Math.max(0, Math.floor((ms || 0) / 1000));
      const mm = String(Math.floor(s / 60)).padStart(2, '0');
      const ss = String(s % 60).padStart(2, '0');
      return `${mm}:${ss}`;
    },

    fmtDuration(ms) {
      const s = Math.max(0, Math.floor((ms || 0) / 1000));
      const h = Math.floor(s / 3600);
      const m = Math.floor((s % 3600) / 60);
      const sec = s % 60;
      if (h > 0) {
        return `${h}h ${String(m).padStart(2, '0')}m`;
      }
      return `${m}m ${String(sec).padStart(2, '0')}s`;
    },

    sessionDurationMs(user, meetingEndedOn) {
      const sessions = Object.values(user.intIds || {});
      if (!sessions.length) return 0;
      const firstJoin = Math.min(...sessions.map((s) => s.registeredOn));
      const lastLeft = Math.max(
        ...sessions.map((s) => s.leftOn || meetingEndedOn || Date.now())
      );
      return Math.max(0, lastLeft - firstJoin);
    },

    webcamTimeMs(user) {
      const cams = (user && user.webcams) || [];
      return cams.reduce(
        (acc, w) => acc + Math.max(0, (w.stoppedOn || 0) - (w.startedOn || 0)),
        0
      );
    },

    realPresentations(slides) {
      const list = slides || [];
      const seen = new Set();
      return list
        .filter((s) => s.presentationName !== 'default.pdf')
        .filter((s) => {
          if (seen.has(s.presentationId)) return false;
          seen.add(s.presentationId);
          return true;
        });
    },

    presentationPageCount(slides, presentationId) {
      const list = slides || [];
      return list.filter((s) => s.presentationId === presentationId).length;
    },

    totalScreenshareMs(shares) {
      return (shares || []).reduce(
        (acc, s) => acc + Math.max(0, (s.stoppedOn || 0) - (s.startedOn || 0)),
        0
      );
    },
  },
  beforeDestroy() {
    this.stopAnalyticsPolling();
  },
};

export default meetingAnalyticsMixin;
